import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { ArrowLeft, Save } from 'lucide-react';

interface Campaign {
  id: string;
  name: string;
  description: string | null;
  headerColor: string | null;
  numberOfWinners: number;
}

export default function CampaignSettingsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editable fields
  const [headerColor, setHeaderColor] = useState('#0891b2');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (campaignId) {
      loadCampaign();
    }
  }, [campaignId]);

  useEffect(() => {
    if (campaign) {
      const colorChanged = headerColor !== (campaign.headerColor || '#0891b2');
      setHasChanges(colorChanged);
    }
  }, [headerColor, campaign]);

  const loadCampaign = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: Campaign }>(
        `/admin/campaigns/${campaignId}`
      );
      const data = response.data.data;
      setCampaign(data);
      setHeaderColor(data.headerColor || '#0891b2');
    } catch (error) {
      console.error('Failed to load campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!campaign) return;

    setSaving(true);
    try {
      const response = await apiClient.put<{ success: boolean; data: Campaign }>(
        `/admin/campaigns/${campaign.id}/settings`,
        { headerColor }
      );
      setCampaign(response.data.data);
      setMessage({ type: 'success', text: 'Settings saved successfully' });
      setHasChanges(false);
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to save settings' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    navigate('/admin/campaigns');
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (!campaign) {
    return <div className="text-red-600">Campaign not found</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button onClick={goBack} className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Campaign Settings</h1>
            <p className="text-sm text-gray-500">{campaign.name}</p>
          </div>
        </div>
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Settings */}
        <div className="space-y-6">
          {/* Header Color */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Flyer Design</h2>
            <p className="text-sm text-gray-500 mb-4">
              These settings affect all QR code flyers for this campaign.
            </p>

            <div className="space-y-4">
              <div>
                <label className="label">Header Background Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={headerColor}
                    onChange={(e) => setHeaderColor(e.target.value)}
                    className="w-12 h-12 rounded-lg border border-gray-200 cursor-pointer p-1"
                  />
                  <input
                    type="text"
                    value={headerColor}
                    onChange={(e) => setHeaderColor(e.target.value)}
                    className="input w-28 font-mono text-sm"
                    placeholder="#0891b2"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  This color appears in the header of all QR code flyers for this campaign.
                </p>
              </div>
            </div>
          </div>

          {/* Campaign Info (read-only) */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Info</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-gray-900">{campaign.name}</p>
              </div>
              {campaign.description && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Description</label>
                  <p className="text-gray-900">{campaign.description}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500">Default Winners</label>
                <p className="text-gray-900">{campaign.numberOfWinners}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Header Preview</h2>
          <p className="text-sm text-gray-500 mb-4">
            Preview of how the header will appear on QR code flyers.
          </p>

          <div
            className="rounded-lg p-6 text-white text-center"
            style={{ backgroundColor: headerColor }}
          >
            <h3 className="font-bold text-2xl">Discover The Park</h3>
            <p className="text-white/80 text-sm mt-1">Scan. Enter. Win!</p>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> Changes to this color will affect all stores participating in this campaign.
              Each store's banner at the bottom of the flyer uses their own store colors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
